import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import StatusBadge from "@/components/base/StatusBadge";
import EmptyState from "@/components/base/EmptyState";
import { useAuth } from "@/context/AuthContext";
import { getAdminBusinessById, getAdminLocations, getAdminPlans, saveAdminBusiness } from "@/services/adminApi";
import { getCategories } from "@/services/publicApi";
import PhotoManager from "@/pages/admin/businesses/components/PhotoManager";
import type { AdminBusinessDetail, AdminLocation, BusinessPhoto, Category, Plan } from "@/types";

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";
const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500";

const STATUS_OPTIONS: AdminBusinessDetail["status"][] = ["approved", "pending", "rejected"];

const MAX_DESC = 600;

interface ToggleProps {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  tone?: "primary" | "secondary";
}

function Toggle({ label, hint, checked, onChange, tone = "primary" }: ToggleProps) {
  const activeClass = tone === "secondary" ? "bg-secondary-500" : "bg-primary-500";
  return (
    <label className="flex cursor-pointer items-start justify-between gap-4 border-t border-background-100 py-4 first:border-t-0">
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-foreground-900">{label}</span>
        <span className="mt-0.5 block text-xs text-foreground-500">{hint}</span>
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors cursor-pointer ${
          checked ? activeClass : "bg-background-300"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-background-50 transition-transform ${
            checked ? "translate-x-5" : "translate-x-0.5"
          }`}
        ></span>
      </button>
    </label>
  );
}

export default function AdminBusinessEditor() {
  const { id } = useParams();
  const { token } = useAuth();

  const [form, setForm] = useState<AdminBusinessDetail | null>(null);
  const [gallery, setGallery] = useState<BusinessPhoto[]>([]);
  // Options for the taxonomy selects (categories, plans, towns).
  const [categoryOptions, setCategoryOptions] = useState<Category[]>([]);
  const [planOptions, setPlanOptions] = useState<Plan[]>([]);
  const [locationOptions, setLocationOptions] = useState<AdminLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    getCategories().then(setCategoryOptions).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!token) return;
    getAdminPlans(token).then(setPlanOptions).catch(() => undefined);
    getAdminLocations(token).then(setLocationOptions).catch(() => undefined);
  }, [token]);

  useEffect(() => {
    if (!id) return;
    let active = true;
    setLoading(true);
    setNotFound(false);
    setLoadError(false);
    getAdminBusinessById(id, token)
      .then((biz) => {
        if (!active) return;
        if (!biz) {
          setNotFound(true);
          return;
        }
        setForm(biz);
        setGallery(biz.gallery ?? []);
      })
      .catch(() => active && setLoadError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [id, token]);

  const patch = (part: Partial<AdminBusinessDetail>) => {
    setForm((f) => (f ? { ...f, ...part } : f));
    setNotice("");
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setError("");
    setNotice("");
    setSaving(true);
    const ok = await saveAdminBusiness(form.id, { ...form, gallery }, token);
    setSaving(false);
    if (!ok) {
      setError("Couldn't save this listing. Please try again.");
      return;
    }
    setNotice("Listing saved.");
    setTimeout(() => setNotice(""), 2600);
  };

  if (loading) {
    return (
      <>
        <Seo title="Edit Business" path="/admin/businesses" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 h-96 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
          <div className="h-72 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
        </div>
      </>
    );
  }

  if (notFound || loadError || !form) {
    return (
      <>
        <Seo title="Edit Business" path="/admin/businesses" />
        <EmptyState
          icon="ri-store-2-line"
          title={loadError ? "Couldn't load this listing" : "Listing not found"}
          description={
            loadError
              ? "Please refresh and try again."
              : "This business may have been removed. Head back to the businesses list."
          }
          action={{ label: "Back to businesses", to: "/admin/businesses" }}
        />
      </>
    );
  }

  return (
    <>
      <Seo title={`Edit — ${form.name}`} path="/admin/businesses" />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Link
            to="/admin/businesses"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-600 hover:text-primary-700 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i> Back to businesses
          </Link>
          <h1 className="mt-2 font-heading text-2xl font-extrabold text-foreground-950">{form.name}</h1>
          <p className="mt-1 text-sm text-foreground-500">
            /{form.slug} · {form.categoryName} · {form.locationName}
          </p>
        </div>
        <StatusBadge status={form.status} />
      </div>

      {(error || notice) && (
        <div
          className={`mt-4 flex items-start gap-3 rounded-md border p-3.5 text-sm ${
            error
              ? "border-secondary-200 bg-secondary-50 text-secondary-900"
              : "border-accent-200 bg-accent-50 text-accent-900"
          }`}
        >
          <i
            className={`mt-0.5 text-lg ${error ? "ri-error-warning-line text-secondary-600" : "ri-checkbox-circle-line text-accent-600"}`}
          ></i>
          <span>{error || notice}</span>
        </div>
      )}

      <form onSubmit={submit} className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">Business details</h2>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label htmlFor="name" className={labelClass}>Business name</label>
                <input id="name" name="name" value={form.name} onChange={(e) => patch({ name: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label htmlFor="categoryId" className={labelClass}>Category</label>
                <select
                  id="categoryId"
                  name="categoryId"
                  value={String(form.categoryId ?? categoryOptions.find((c) => c.name === form.categoryName)?.id ?? "")}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    const match = categoryOptions.find((c) => String(c.id) === e.target.value);
                    patch({ categoryId: next, categoryName: match?.name ?? form.categoryName });
                  }}
                  className={`${inputClass} cursor-pointer`}
                >
                  {categoryOptions.length === 0 && <option value={String(form.categoryId ?? "")}>{form.categoryName}</option>}
                  {categoryOptions.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="locationId" className={labelClass}>Location</label>
                <select
                  id="locationId"
                  name="locationId"
                  value={String(form.locationId ?? locationOptions.find((l) => l.name === form.locationName)?.id ?? "")}
                  onChange={(e) => {
                    const match = locationOptions.find((l) => String(l.id) === e.target.value);
                    patch({ locationId: e.target.value ? Number(e.target.value) : null, locationName: match?.name ?? form.locationName });
                  }}
                  className={`${inputClass} cursor-pointer`}
                >
                  {locationOptions.length === 0 && <option value={String(form.locationId ?? "")}>{form.locationName}</option>}
                  {locationOptions.map((l) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between">
                  <label htmlFor="description" className={labelClass}>Description</label>
                  <span className="text-[11px] text-foreground-400">{form.description.length}/{MAX_DESC}</span>
                </div>
                <textarea
                  id="description"
                  name="description"
                  rows={5}
                  maxLength={MAX_DESC}
                  value={form.description}
                  onChange={(e) => patch({ description: e.target.value.slice(0, MAX_DESC) })}
                  className={`${inputClass} resize-none`}
                ></textarea>
              </div>
            </div>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">Contact &amp; location</h2>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="phone" className={labelClass}>Phone</label>
                <input id="phone" name="phone" value={form.phone} onChange={(e) => patch({ phone: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label htmlFor="email" className={labelClass}>Email</label>
                <input id="email" name="email" type="email" value={form.email} onChange={(e) => patch({ email: e.target.value })} className={inputClass} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="website" className={labelClass}>Website</label>
                <input id="website" name="website" value={form.website ?? ""} onChange={(e) => patch({ website: e.target.value })} placeholder="https:// (optional)" className={inputClass} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="address" className={labelClass}>Address</label>
                <input id="address" name="address" value={form.address} onChange={(e) => patch({ address: e.target.value })} className={inputClass} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="hours" className={labelClass}>Opening hours</label>
                <input id="hours" name="hours" value={form.hours} onChange={(e) => patch({ hours: e.target.value })} placeholder="e.g. 9:00 AM - 8:00 PM" className={inputClass} />
              </div>
            </div>
          </section>

          <PhotoManager photos={gallery} onChange={setGallery} token={token} disabled={saving} />
        </div>

        <aside className="space-y-4">
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-sm font-bold text-foreground-950">Plan &amp; visibility</h2>
            <div className="mt-4">
              <label htmlFor="planId" className={labelClass}>Listing plan</label>
              <select
                id="planId"
                name="planId"
                value={String(form.planId ?? planOptions.find((p) => p.name === form.plan)?.id ?? "")}
                onChange={(e) => {
                  const match = planOptions.find((p) => String(p.id) === e.target.value);
                  patch({ planId: Number(e.target.value), plan: match?.name ?? form.plan });
                }}
                className={`${inputClass} cursor-pointer`}
              >
                {planOptions.length === 0 && <option value={String(form.planId ?? "")}>{form.plan}</option>}
                {planOptions.map((plan) => (
                  <option key={plan.id} value={plan.id}>{plan.name}</option>
                ))}
              </select>
            </div>
            <div className="mt-4">
              <label htmlFor="status" className={labelClass}>Status</label>
              <select
                id="status"
                name="status"
                value={form.status}
                onChange={(e) => patch({ status: e.target.value as AdminBusinessDetail["status"] })}
                className={`${inputClass} cursor-pointer`}
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            <div className="mt-2">
              <Toggle
                label="Featured"
                hint="Highlight on the homepage and featured listings."
                checked={form.isFeatured}
                onChange={(v) => patch({ isFeatured: v })}
              />
              <Toggle
                label="Verified"
                hint="Show the verified badge on the public profile."
                checked={form.isVerified}
                onChange={(v) => patch({ isVerified: v })}
                tone="secondary"
              />
            </div>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-100/60 p-5">
            <h2 className="font-heading text-sm font-bold text-foreground-950">At a glance</h2>
            <dl className="mt-3 space-y-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">Rating</dt>
                <dd className="font-semibold text-foreground-800">{form.rating.toFixed(1)} / 5</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">Reviews</dt>
                <dd className="font-semibold text-foreground-800">{form.reviewCount.toLocaleString()}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">Profile views</dt>
                <dd className="font-semibold text-foreground-800">{form.views.toLocaleString()}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">Owner</dt>
                <dd className="max-w-[60%] truncate font-semibold text-foreground-800">{form.owner}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">Listed</dt>
                <dd className="font-semibold text-foreground-800">{form.createdAt}</dd>
              </div>
            </dl>
          </section>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap cursor-pointer"
          >
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
            {saving ? "Saving…" : "Save changes"}
          </button>
        </aside>
      </form>
    </>
  );
}